// src/components/registration/BHWaiverForm.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { styles } from '../../styles/theme';
import { PublicHeader } from '../shared/Header';
import { Checkbox } from '../shared/FormField';

const BHWaiverForm = () => {
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    grade: '',
    homeAddress: '',
    authorizedPickup: '',
    emergencyContact: '',
    photoRelease: false,
    termsAgreed: false,
    parentSignature: '',
    additionalNotes: '',
    foodAllergies: '',
    otherAllergies: '',
    medicalConditions: ''
  });
  
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadStudentData();
  }, []);

  const loadStudentData = async () => {
    try {
      // Get student ID from URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const studentId = urlParams.get('id');
      
      if (!studentId) {
        setError('Invalid registration link. Please use the link from your email.');
        setLoading(false);
        return;
      }

      // Load student data from Firestore
      const studentRef = doc(db, 'students', studentId);
      const studentDoc = await getDoc(studentRef);

      if (!studentDoc.exists()) {
        setError('Student record not found. Please contact Stemtree at (425) 559-7836.');
        setLoading(false);
        return;
      }

      const data = studentDoc.data();
      
      // Check if already completed
      if (data.registrationComplete) {
        setSubmitted(true);
      }
      
      setStudentData({ id: studentDoc.id, ...data });
      
      // Pre-fill medical information from BH data
      setFormData(prev => ({
        ...prev,
        foodAllergies: data.foodAllergies || 'None',
        otherAllergies: data.otherAllergies || 'None',
        medicalConditions: data.medicalConditions || 'None'
      }));
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading student data:', err);
      setError('Failed to load registration. Please try again or contact Stemtree.');
      setLoading(false);
    }
  };

  const showAgreement = (type) => {
    const agreementText = type === 'terms' 
      ? `AGREEMENT TERMS

Parental Consent: By enrolling my child(ren) in any Stemtree program, I agree to the following:

Educational Focus – Stemtree is an educational center focused on science and engineering. It is not a daycare or babysitting service.

Deposit – The first month's payment acts as a non-refundable deposit to reserve a spot.

Payments – Billing is handled through Bright Horizons. Parents must follow BH's payment procedures.

Cancellation – Follow Bright Horizons' cancellation policy. Notify both BH and Stemtree of any changes.

Refunds – No refunds or make-ups are provided for absences due to illness, vacations, or other personal schedule conflicts.

Absence Notification – For safety, parents must call or email Stemtree before drop-off time if a child will be absent.

Facility Policy – Stemtree is a drop-in educational facility. Children are free to come and go at the direction of their parent/guardian, not at the direction of Stemtree.

Program Schedule:
Camp Days – Full-day camp runs from 9:00 AM - 5:00 PM on scheduled days only.

No Camp Days – There is no service on statutory holidays, inclement weather closures, or any unscheduled closures. Parents must have a back-up plan for these situations.

Parents must provide correct emergency contact information and update any changes to medical conditions or allergies.`
      : `PRIVACY POLICY

Stemtree of Redmond is committed to protecting your privacy and the personal information you share with us.

Information Collection: We collect only the information necessary to provide our educational services, including student names, parent contact information, and medical information relevant to your child's safety.

Information Use: Your information is used solely for program administration, emergency contacts, and communication about Stemtree programs and activities.

Information Sharing: We do not sell, trade, or share your personal information with third parties except as required by law, with Bright Horizons for billing purposes, or with your explicit consent.

Security: We maintain appropriate security measures to protect your information from unauthorized access.

Your Rights: You may request to review, update, or delete your personal information at any time by contacting us.

For questions about our privacy practices, contact us at redmond@stemtree.com`;

    alert(agreementText);
  };

  const validate = () => {
    const errors = {};
    
    if (!formData.grade) {
      errors.grade = 'Please select student grade';
    }
    
    if (!formData.homeAddress || formData.homeAddress.trim().length < 10) {
      errors.homeAddress = 'Please enter complete home address';
    }
    
    if (!formData.authorizedPickup || formData.authorizedPickup.trim().length < 5) {
      errors.authorizedPickup = 'Please provide at least one authorized pickup person';
    }
    
    if (!formData.emergencyContact || formData.emergencyContact.trim().length < 10) {
      errors.emergencyContact = 'Please provide emergency contact information';
    }
    
    if (!formData.termsAgreed) {
      errors.termsAgreed = 'You must agree to the terms and conditions';
    }
    
    if (!formData.parentSignature || formData.parentSignature.trim().length < 2) {
      errors.parentSignature = 'Please enter your full name as signature';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      alert('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Update student record in Firestore
      const studentRef = doc(db, 'students', studentData.id);
      await updateDoc(studentRef, {
        grade: formData.grade,
        homeAddress: formData.homeAddress,
        authorizedPickup: formData.authorizedPickup,
        emergencyContact: formData.emergencyContact,
        foodAllergies: formData.foodAllergies,
        otherAllergies: formData.otherAllergies,
        medicalConditions: formData.medicalConditions,
        registrationComplete: true,
        photoRelease: formData.photoRelease,
        termsAgreed: formData.termsAgreed,
        parentSignature: formData.parentSignature,
        additionalNotes: formData.additionalNotes,
        waiverCompletedDate: new Date().toISOString(),
        status: 'confirmed'
      });

      setSubmitted(true);
      
      // Scroll to top to show success message
      window.scrollTo(0, 0);
      
    } catch (error) {
      console.error('Waiver submission error:', error);
      alert('Failed to submit waiver. Please try again or contact Stemtree at (425) 559-7836.');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p style={{ color: '#6b7280' }}>Loading registration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <PublicHeader />
        <div style={{ maxWidth: '600px', margin: '4rem auto', padding: '0 1rem' }}>
          <div style={{ ...styles.card, backgroundColor: '#fee2e2', border: '2px solid #dc2626' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <AlertCircle style={{ width: '24px', height: '24px', color: '#dc2626', flexShrink: 0 }} />
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', color: '#dc2626' }}>Error</h2>
                <p style={{ margin: 0, color: '#991b1b' }}>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted || studentData?.registrationComplete) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <PublicHeader />
        <div style={{ maxWidth: '600px', margin: '4rem auto', padding: '0 1rem' }}>
          <div style={{ ...styles.card, backgroundColor: '#d1fae5', border: '2px solid #10b981' }}>
            <div style={{ textAlign: 'center' }}>
              <CheckCircle style={{ width: '64px', height: '64px', color: '#10b981', margin: '0 auto 1rem auto' }} />
              <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#065f46', margin: '0 0 1rem 0' }}>
                Registration Complete! ✓
              </h1>
              <p style={{ fontSize: '1.125rem', color: '#047857', marginBottom: '2rem' }}>
                Thank you for completing the waiver for {studentData?.firstName} {studentData?.lastName}.
              </p>
              <div style={{ backgroundColor: '#ecfdf5', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                <h3 style={{ color: '#065f46', margin: '0 0 1rem 0' }}>What's Next?</h3>
                <ul style={{ textAlign: 'left', color: '#047857', lineHeight: '1.8' }}>
                  <li>You're all set for camp!</li>
                  <li>We'll see you on your first scheduled day</li>
                  <li>Drop-off: 9:00 AM | Pick-up: 5:00 PM</li>
                  <li>Remember to bring nut-free snacks and a water bottle</li>
                </ul>
              </div>
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Questions? Call us at (425) 559-7836 or email redmond@stemtree.com
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <PublicHeader />

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
        {/* Header */}
        <div style={{
          ...styles.card,
          marginBottom: '2rem',
          background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
          color: 'white'
        }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', margin: '0 0 0.5rem 0' }}>
            Complete Your Camp Registration
          </h1>
          <p style={{ margin: 0, fontSize: '1rem', opacity: 0.95 }}>
            Bright Horizons Full-Day Camp
          </p>
        </div>

        {/* Student Info (Read-Only) */}
        <div style={{ ...styles.card, marginBottom: '1.5rem', backgroundColor: '#f0f9ff', border: '2px solid #00A3E0' }}>
          <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold', color: '#0c4a6e' }}>
            ✓ Student Information (From Bright Horizons)
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Student Name</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0c4a6e' }}>
                {studentData?.firstName} {studentData?.lastName}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Date of Birth</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0c4a6e' }}>
                {studentData?.dateOfBirth || 'N/A'}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Gender</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0c4a6e' }}>
                {studentData?.gender || 'Not specified'}
              </div>
            </div>
          </div>

          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #bae6fd' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem' }}>Scheduled Camp Dates</div>
            <div style={{ fontSize: '0.875rem', color: '#0c4a6e' }}>
              {studentData?.careDates && studentData.careDates.length > 0 ? (
                studentData.careDates.map((cd, idx) => (
                  <div key={idx} style={{ marginBottom: '0.25rem' }}>
                    • {cd.date} ({cd.startTime} - {cd.endTime})
                  </div>
                ))
              ) : (
                'Dates to be confirmed'
              )}
            </div>
          </div>
        </div>

        {/* Parent Info (Read-Only) */}
        <div style={{ ...styles.card, marginBottom: '1.5rem', backgroundColor: '#f0f9ff', border: '2px solid #00A3E0' }}>
          <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold', color: '#0c4a6e' }}>
            ✓ Parent/Guardian Information (From Bright Horizons)
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Parent Name</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0c4a6e' }}>
                {studentData?.parentName || 'N/A'}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Email</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0c4a6e' }}>
                {studentData?.parentEmail || 'N/A'}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Phone</div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#0c4a6e' }}>
                {studentData?.parentPhone || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Medical Information - EDITABLE */}
          <div style={{
            ...styles.card,
            backgroundColor: '#fef3c7',
            border: '2px solid #f59e0b',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{
              color: '#92400e',
              fontSize: '1.25rem',
              fontWeight: 'bold',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              ⚠️ Medical Information
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '1.5rem' }}>
              Please review and update this information if anything has changed since your Bright Horizons authorization.
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#92400e',
                marginBottom: '0.5rem'
              }}>
                Food Allergies
              </label>
              <input
                type="text"
                value={formData.foodAllergies}
                onChange={(e) => setFormData({ ...formData, foodAllergies: e.target.value })}
                placeholder="None"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #fbbf24',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: 'white'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#92400e',
                marginBottom: '0.5rem'
              }}>
                Other Allergies
              </label>
              <input
                type="text"
                value={formData.otherAllergies}
                onChange={(e) => setFormData({ ...formData, otherAllergies: e.target.value })}
                placeholder="None"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #fbbf24',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: 'white'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#92400e',
                marginBottom: '0.5rem'
              }}>
                Medical Conditions
              </label>
              <textarea
                value={formData.medicalConditions}
                onChange={(e) => setFormData({ ...formData, medicalConditions: e.target.value })}
                placeholder="None"
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #fbbf24',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: 'white',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          {/* Missing Information - Parent needs to fill */}
          <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
              📝 Additional Required Information
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              Please provide the following information not included in your Bright Horizons authorization:
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#374151'
              }}>
                Student's Grade *
              </label>
              <select
                value={formData.grade}
                onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${formErrors.grade ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select Grade</option>
                <option value="Pre-K">Pre-K</option>
                <option value="Kindergarten">Kindergarten</option>
                <option value="1st Grade">1st Grade</option>
                <option value="2nd Grade">2nd Grade</option>
                <option value="3rd Grade">3rd Grade</option>
                <option value="4th Grade">4th Grade</option>
                <option value="5th Grade">5th Grade</option>
                <option value="6th Grade">6th Grade</option>
                <option value="7th Grade">7th Grade</option>
                <option value="8th Grade">8th Grade</option>
              </select>
              {formErrors.grade && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {formErrors.grade}
                </p>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#374151'
              }}>
                Home Address *
              </label>
              <input
                type="text"
                value={formData.homeAddress}
                onChange={(e) => setFormData({ ...formData, homeAddress: e.target.value })}
                placeholder="Street address, City, State, ZIP"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${formErrors.homeAddress ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              {formErrors.homeAddress && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {formErrors.homeAddress}
                </p>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#374151'
              }}>
                Authorized Pickup Persons *
              </label>
              <input
                type="text"
                value={formData.authorizedPickup}
                onChange={(e) => setFormData({ ...formData, authorizedPickup: e.target.value })}
                placeholder="Names and phone numbers of people authorized to pick up your child (e.g., Jane Smith 425-555-1234, John Doe 206-555-5678)"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${formErrors.authorizedPickup ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              {formErrors.authorizedPickup && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {formErrors.authorizedPickup}
                </p>
              )}
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                List all people authorized to pick up your child. We will check ID at pickup.
              </p>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#374151'
              }}>
                Emergency Contact (Besides Parent) *
              </label>
              <input
                type="text"
                value={formData.emergencyContact}
                onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
                placeholder="Name, relationship, and phone number (e.g., Grandma Mary Smith 425-555-9999)"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${formErrors.emergencyContact ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              {formErrors.emergencyContact && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {formErrors.emergencyContact}
                </p>
              )}
            </div>
          </div>

          {/* Waivers & Signatures */}
          <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
              📋 Required Agreements
            </h2>

            <Checkbox
              checked={formData.photoRelease}
              onChange={(e) => setFormData({ ...formData, photoRelease: e.target.checked })}
            >
              I consent to photographs/videos of my child being used for marketing purposes
            </Checkbox>

            <Checkbox
              checked={formData.termsAgreed}
              onChange={(e) => setFormData({ ...formData, termsAgreed: e.target.checked })}
              error={formErrors.termsAgreed}
            >
              I agree to the{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  showAgreement('terms');
                }}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Terms and Conditions
              </button>
              {' '}and{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  showAgreement('privacy');
                }}
                style={{ 
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Privacy Policy
              </button>
              {' '}*
            </Checkbox>

            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#374151'
              }}>
                Parent/Guardian Signature (Type Full Name) *
              </label>
              <input
                type="text"
                value={formData.parentSignature}
                onChange={(e) => setFormData({ ...formData, parentSignature: e.target.value })}
                placeholder="John Doe"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${formErrors.parentSignature ? '#dc2626' : '#d1d5db'}`,
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontFamily: 'cursive'
                }}
              />
              {formErrors.parentSignature && (
                <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  {formErrors.parentSignature}
                </p>
              )}
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: '600', 
                marginBottom: '0.5rem',
                color: '#374151'
              }}>
                Additional Notes or Special Instructions (Optional)
              </label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                placeholder="Any additional information you'd like us to know..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.button,
              width: '100%',
              padding: '1.25rem',
              fontSize: '1.125rem',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Complete Registration ✓'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BHWaiverForm;